import { createTheme, Theme, ThemeOptions, ThemeProvider } from '@mui/material/styles';
import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { helpingMethod } from '../utils/HelpingMethod';
import {
  borderRadius,
  customShadows,
  zIndexTokens,
  customTransitions,
  componentSizes,
  modalWidths,
  drawerWidths,
} from './tokens';

// Type for Color Mode Context
interface ColorModeContextType {
  toggleColorMode: () => void;
  mode: 'light' | 'dark';
}
// Create a theme context with default values
export const ColorModeContext = createContext<ColorModeContextType>({
  toggleColorMode: () => {},
  mode: 'light',
});

const CHIP_TYPES = ['STANDARD', 'ACQUISITION', 'SUCCESS', 'ERROR', 'DANGER', 'NEW', 'WARNING'];

const CHIP_COLOR_PROPS = {
  BLUE: '#5BA9F1',
  PURPLE: '#9747FF',
  TEAL: '#00CEB6',
  PINK_BG: '#FFD9E4',
  PINK_TEXT: '#FF6692',
  RED: '#FF4444',
  GREEN: '#00C851',
  ORANGE: '#FF8800',
  DEFAULT_BG: '#E5E7EB',
  DEFAULT_TEXT: '#111928',
} as const;

// Theme options for light and dark modes
const getThemeOptions = (): ThemeOptions => ({
  palette: {
    // Common colors
    common: {
      white: '#FFFFFF',
      black: '#000000',
    },
    primary: {
      main: '#00a1ff',
      light: '#4B83FF',
      dark: '#000008',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#111928',
      light: '#374151',
      dark: '#0B111B',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F9FAFB',
      paper: '#FFFFFF',
    },
    loginPage: {
      primary: '#000F42',
      secondary: '#FFFFFF',
    },
    text: {
      primary: '#111928',
      secondary: '#6B7280',
    },
    shadow: {
      cardShadow: '#101E360A',
      cardContent: '#8592ad33',
      modalPaper: '0px 10px 30px rgba(0,0,0,0.15)',
    },
    divider: '#E5E7EB',
    datagridHeader: '#F5F6FA',
    datagridHeaderAlt: '#E4EBF099',
    datagridRow: '#FFFFFF',
    datagridPagination: '#F5F6FA',
    datagridVerticalBorder: '#999999ff',
    datagridHorizontalBorder: '#abadb0ff',
    sideDrawerHeader: '#dcdde4ff',
    sideDrawerBody: '#cecfd4ff',
    sideDrawerBottom: '#434343ff',
    dialogPaper: '#d0ceceff',
    header: {
      background: '#F8FAFD',
    },
    // Left Navigation
    leftNavigation: {
      title: '#111C2D',
      activeText: '#00A1FF',
      activeBg: '#D9F1FF',
      text: '#6B7280',
    },
    // Custom action/menu colors
    menuIconHoverBg: '#c5eaff',
    menuItemHoverBg: '#D9F1FF',
    menuIconColor: 'rgba(17, 28, 45, 0.6)',
    // Custom pagination colors
    paginationSelectedBg: '#0095ff',
    paginationSelectedHoverBg: '#007bdb',
    paginationDisabledText: '#d3d3d3',
    paginationBorder: '#e0e0e0',
    paginationEllipsis: '#999999',
    // Button colors
    primaryHover: '#0088CE',
    buttonDisabledBg: '#9CA3AF',
    buttonDisabledText: '#ffffff',
    borderDefault: '#e0e0e0',
    borderLight: '#ccc',
    buttonRed: '#ef0909ff',
    success: { main: '#0E9F6E' },
    error: { main: '#F05252' },
    warning: { main: '#FFB400' },
    warningAmber: '#F59E0B',
    info: { main: '#3F83F8' },

    // Components
    checkbox: {
      border: '#DFE5EF',
      hoverBg: '#F5F8FF',
    },
    chips: CHIP_TYPES.reduce(
      (acc, type) => ({
        ...acc,
        [type]: helpingMethod.getChipColors(type, {}, CHIP_COLOR_PROPS),
      }),
      {},
    ),
    // Dropdown component
    dropdown: {
      border: '#E4EBF0',
      focusBorder: '#0993DD',
      font: '#111C2D',
      placeholder: 'rgba(17, 28, 45, 0.4)',
      bg: '#FFFFFF',
      icon: '#6B7280',
      itemHoverBg: '#F5F8FF',
      itemSelectedBg: '#F0F9FF',
      menuBorder: '#E5E7EB',
      helperTextError: '#EF4444',
      helperTextDefault: '#6B7280',
    },
    // InputField component
    inputField: {
      border: '#E4EBF0',
      hoverBorder: '#B6C2CD',
      focusBorder: '#0993DD',
      errorBorder: '#FF3C61',
      font: '#111C2D',
      placeholder: 'rgba(17, 28, 45, 0.4)',
      background: '#FFFFFF',
    },
    // DatePicker component
    datePicker: {
      border: '#E5E7EB',
      headerBlue: '#3B82F6',
      weekdayGray: '#6B7280',
      dayText: '#111827',
      hoverBg: '#F3F4F6',
      selectedBg: '#D9F1FF',
      selectedText: '#3B82F6',
      todayText: '#3B82F6',
    },
    // FormField component
    formField: {
      label: '#374151',
      helperDefault: '#6B7280',
      helperError: '#EF4444',
    },
    // FileUploader component
    fileUploader: {
      borderDefault: '#444444',
      borderActive: '#2196f3',
      borderError: '#e53935',
      hoverError: '#c62828',
      hoverActive: '#1976d2',
      filenameBlue: '#2196f3',
      iconBlue: '#1976D2',
      uploadText: '#36a2f7',
      textMuted: '#aaaaaa',
      textLight: '#dddddd',
    },
    // Modal component
    modal: {
      title: '#111C2D',
      subtitle: '#6B7280',
      closeIcon: '#6B7280',
      error: '#EF4444',
    },
    // Scrollbar tokens
    scrollbar: {
      thumb: '#888',
      thumbHover: '#a8a8a8',
      track: '#f1f1f1',
    },
    // Pill component
    pill: {
      background: '#F3F4F6',
      text: '#374151',
    },
    // Icon colors
    icons: {
      warning: '#FFB900',
    },
    // RadioButton component
    radio: {
      borderChecked: '#0993DD',
      borderDefault: '#E4EBF0',
      background: '#FFFFFF',
      dot: '#0993DD',
      label: 'rgba(17,28,45,0.8)',
    },
    // SideDrawer component
    sideDrawer: {
      border: '#E5E7EB',
      title: '#111827',
      icon: '#6B7280',
      shadow: '0px 10px 30px rgba(0,0,0,0.05)',
    },
    // TextField component
    textField: {
      border: '#B6C2CD',
    },
    // Product Add Flow (Step 1)
    productAddFlow: {
      statusBadgeBg: '#FCE7F3',
      statusBadgeText: '#BE185D',
      heading: '#111C2D',
      subheading: '#111C2DCC',
    },
    avatar: {
      bg: '#E8D4C4',
      text: '#5D4037',
    },
    textMedium: '#666',
  },
  typography: {
    fontSize: 16,
    fontFamily: '"Inter", sans-serif',
    h1: {
      fontSize: '3.938rem',
      letterSpacing: '0em',
      fontWeight: 'bold',
    },
    h2: {
      fontSize: '3.313rem',
      letterSpacing: '-0.043em',
      fontWeight: 'bold',
    },
    h3: {
      fontSize: '2.375rem',
      letterSpacing: '-0.043em',
      fontWeight: 'bold',
    },
    h4: {
      fontSize: '1.375rem',
      letterSpacing: '-0.016em',
      fontWeight: 'bold',
    },
    button: {
      fontSize: '1.125rem',
      fontWeight: 300,
      letterSpacing: '0em',
    },
    subtitle1: {
      fontSize: '1.375rem',
      letterSpacing: '-0.011em',
      fontWeight: 'bold',
    },
    subtitle2: {
      fontSize: '1.25rem',
      letterSpacing: '-0.011em',
    },
    body1: {
      fontSize: '1.125rem',
      letterSpacing: '-0.013em',
    },
    body2: {
      fontSize: '1rem',
      letterSpacing: '-0.011em',
    },
    small: {
      fontSize: '0.875rem',
      letterSpacing: '-0.011em',
    },
    caption1: {
      fontSize: '0.75rem',
      letterSpacing: '-0.011em',
    },
    caption2: {
      fontSize: '0.625rem',
      letterSpacing: '-0.011em',
    },
  },
  // Structural design tokens
  borderRadius,
  customShadows,
  zIndex: {
    ...zIndexTokens,
  },
  componentSizes,
  modalWidths,
  drawerWidths,
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme: Theme) => ({
        body: {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          fontFamily: theme.typography.fontFamily,
          transition: customTransitions.default,
        },
      }),
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          transition: customTransitions.default,
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: theme.borderRadius.md,
          textTransform: 'none' as const,
          fontWeight: 500,
        }),
      },
    },
  },
});

// Custom hook to use the color mode context
export const useColorMode = () => {
  return useContext(ColorModeContext);
};

// Props for PrimaryTheme
interface PrimaryThemeProps {
  children: ReactNode;
}

// Main theme provider component
const PrimaryTheme: React.FC<PrimaryThemeProps> = ({ children }) => {
  // State to track the current theme mode
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  // Color mode context value with toggle function
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      mode,
    }),
    [mode],
  );

  // Generate the theme based on current mode
  // NOTE: MUI's createTransitions() strips unknown keys, so we attach
  // `custom` onto `transitions` after theme creation.
  const theme = useMemo(() => {
    const t = createTheme(getThemeOptions());
    (t.transitions as any).custom = customTransitions;
    return t;
  }, []);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default PrimaryTheme;
